import React, { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
  proposalService,
  type ActivityLibraryItem,
  type ActivityCategory,
} from '@/services/ProposalService';
import { Check, Clock, Search, Sparkles } from 'lucide-react';

const CATEGORY_OPTIONS: { value: ActivityCategory; label: string; emoji: string }[] = [
  { value: 'culture', label: 'Culture', emoji: '🏛️' },
  { value: 'adventure', label: 'Adventure', emoji: '🧗' },
  { value: 'food_drink', label: 'Food & Drink', emoji: '🍽️' },
  { value: 'wellness', label: 'Wellness', emoji: '🧘' },
  { value: 'entertainment', label: 'Entertainment', emoji: '🎭' },
  { value: 'nature', label: 'Nature', emoji: '🌳' },
  { value: 'team_building', label: 'Team Building', emoji: '🤝' },
  { value: 'sightseeing', label: 'Sightseeing', emoji: '📸' },
  { value: 'water_sports', label: 'Water Sports', emoji: '🏄' },
  { value: 'other', label: 'Other', emoji: '✨' },
];

function getCategoryInfo(category: ActivityCategory | null) {
  return CATEGORY_OPTIONS.find(c => c.value === category);
}

interface ActivityLibraryBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  city: string;
  country: string;
  onActivityAdded: () => void;
  /**
   * When provided, activities are copied directly into the given research
   * destination (research_destination_activities). Otherwise they go into
   * the city/country destination pool (destination_activities).
   */
  researchDestinationId?: string;
}

export function ActivityLibraryBrowser({
  isOpen,
  onClose,
  city,
  country,
  onActivityAdded,
  researchDestinationId,
}: ActivityLibraryBrowserProps) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ActivityLibraryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ActivityCategory | ''>('');
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setCategoryFilter('');
      setAddedIds(new Set());
      loadLibrary();
    }
  }, [isOpen]);

  async function loadLibrary() {
    setLoading(true);
    try {
      const list = await proposalService.listActivityLibrary(false);
      setItems(list);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(item: ActivityLibraryItem) {
    setAdding(item.id);
    try {
      if (researchDestinationId) {
        await proposalService.copyLibraryActivityToResearch(item.id, researchDestinationId);
      } else {
        await proposalService.copyLibraryActivityToDestination(item.id, city, country);
      }
      setAddedIds(prev => new Set(prev).add(item.id));
      onActivityAdded();
    } catch {
      // handled upstream
    } finally {
      setAdding(null);
    }
  }

  const filtered = items.filter(item => {
    if (categoryFilter && item.category !== categoryFilter) return false;
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(q) || (item.description?.toLowerCase().includes(q));
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add from Activity Library"
      className="max-w-lg"
    >
      <div className="mb-3 flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Input
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
            autoFocus
          />
        </div>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as ActivityCategory | '')}
        >
          <option value="">All</option>
          {CATEGORY_OPTIONS.map(c => (
            <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Sparkles className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">
            {items.length === 0
              ? 'No activities in your library yet. Create them in Organization → Activity Library.'
              : 'No matching activities found.'}
          </p>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto space-y-2">
          {filtered.map(item => {
            const justAdded = addedIds.has(item.id);
            const isAdding = adding === item.id;
            const categoryInfo = getCategoryInfo(item.category);

            return (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                {/* Thumbnail */}
                {item.images?.[0]?.url ? (
                  <img
                    src={item.images[0].url}
                    alt=""
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-gray-400" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{item.name}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {categoryInfo && (
                      <span className="text-xs text-gray-500">{categoryInfo.emoji} {categoryInfo.label}</span>
                    )}
                    {item.duration_hours != null && (
                      <span className="text-xs text-gray-500 flex items-center gap-0.5">
                        <Clock className="w-3 h-3" />
                        {item.duration_hours}h
                      </span>
                    )}
                    {item.estimated_price_per_person != null && (
                      <span className="text-xs text-gray-500">${item.estimated_price_per_person}/pp</span>
                    )}
                  </div>
                </div>

                {/* Action */}
                {justAdded ? (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    <Check className="w-3 h-3" /> Added
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(item)}
                    disabled={isAdding}
                  >
                    {isAdding ? 'Adding...' : 'Add'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button variant="ghost" onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}
